import classNames from 'classnames';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { type ElementType, type FocusEventHandler, useMemo, useState } from 'react';
// We intentionally do NOT use the provided styles from the library, instead opting to style everything ourselves
import PhoneNumberInput, { type Country, getCountryCallingCode, isSupportedCountry } from 'react-phone-number-input';
import de from 'react-phone-number-input/locale/de.json';
import en from 'react-phone-number-input/locale/en.json';
import es from 'react-phone-number-input/locale/es.json';
import fr from 'react-phone-number-input/locale/fr.json';
import it from 'react-phone-number-input/locale/it.json';
import nb from 'react-phone-number-input/locale/nb.json';
import nl from 'react-phone-number-input/locale/nl.json';
import pl from 'react-phone-number-input/locale/pl.json';
import ptBr from 'react-phone-number-input/locale/pt-BR.json';
import pt from 'react-phone-number-input/locale/pt.json';
import sv from 'react-phone-number-input/locale/sv.json';
import { useCountryCode } from '@alltrails/core/hooks/useCountryCode';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import ChevronSm from '../../icons/ChevronSm';
import { InputBaseProps } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import InputField from '../InputField';
import styles from './styles/PhoneInput.module.scss';

// https://gitlab.com/catamphetamine/react-phone-number-input#countryselectcomponent
type CountrySelectProps = {
  disabled?: boolean;
  iconComponent: ElementType;
  onChange: (value: Country | undefined) => void;
  options: { label: string; value?: Country }[];
  value?: Country;
};

const CountrySelect = ({ disabled, iconComponent: Icon, onChange, options, value }: CountrySelectProps) => {
  const selectedOption = useMemo(() => {
    if (!value) {
      return options[0];
    }
    return options.find(o => o.value === value);
  }, [options, value]);
  return (
    <div className={styles.countrySelect}>
      <select
        key="country-select"
        className={styles.select}
        disabled={disabled}
        onChange={e => {
          onChange(e.target.value as Country);
        }}
        value={value}
      >
        {options.map(o => {
          const callingCode = o.value && isSupportedCountry(o.value) ? ` (+${getCountryCallingCode(o.value as Country)})` : '';
          // The "international" option has no value, so we default it to an empty string, else it would use the label which can be translated
          return (
            <option key={o.value || 'International'} value={o.value || ''}>
              {`${o.label}${callingCode}`}
            </option>
          );
        })}
      </select>
      <Icon key="country-select-flag" country={value} label={selectedOption?.label} />
      <ChevronSm key="country-select-chevron" color={disabled ? '--color-text-disabled' : '--color-text-secondary'} orientation="down" />
    </div>
  );
};

type PhoneInputProps = InputBaseProps & {
  className?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange: (value: string) => void;
  required?: boolean;
  value?: string;
};

const PhoneInput = ({
  className,
  disabled,
  errorText,
  helperText,
  hideLabel,
  labelText,
  labelTextVariant,
  onBlur,
  onChange,
  required = false,
  value
}: PhoneInputProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const countryCode = useCountryCode();

  const [country, setCountry] = useState<Country>(countryCode);

  const labels = useMemo(() => {
    switch (languageRegionCode) {
      case 'da-DK':
        // The library does not yet have localization support for Danish https://gitlab.com/catamphetamine/react-phone-number-input#localization
        return nb;
      case 'de-DE':
        return de;
      case 'es-ES':
      case 'es-MX':
        return es;
      case 'fr-FR':
        return fr;
      case 'it-IT':
        return it;
      case 'nb-NO':
        return nb;
      case 'nl-NL':
        return nl;
      case 'pl-PL':
        return pl;
      case 'pt-BR':
        return ptBr;
      case 'pt-PT':
        return pt;
      case 'sv-SE':
        return sv;
      case 'en-GB':
      case 'en-US':
      default:
        return en;
    }
  }, [languageRegionCode]);

  const placeholder = useMemo(() => getExampleNumber(country, examples)?.formatNational(), [country]);

  return (
    <InputField
      {...setDenaliAttribute('PhoneInput')}
      className={className}
      disabled={disabled}
      errorText={errorText}
      helperText={helperText}
      labelText={hideLabel ? '' : labelText}
      labelTextVariant={labelTextVariant}
    >
      <PhoneNumberInput
        aria-label={hideLabel ? labelText : undefined}
        autoComplete="tel"
        className={classNames(styles.inputContainer, { [styles.disabled]: disabled, [styles.hasError]: Boolean(errorText) })}
        countrySelectComponent={CountrySelect}
        defaultCountry={country}
        disabled={disabled}
        focusInputOnCountrySelection
        inputMode="tel"
        labels={labels}
        onCountryChange={(newCountry = 'US') => setCountry(newCountry)}
        onChange={value => onChange(value ? String(value) : '')}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        type="tel"
        value={value}
      />
    </InputField>
  );
};

export default PhoneInput;
export { isValidPhoneNumber } from 'react-phone-number-input';
